import {Button, Group, Modal, Select, Stack, TextInput} from '@mantine/core'
import {useForm} from '@mantine/form'
import {useEffect, useState} from 'react'

import type {CreateRentalAssetInput, RentalAsset, RentalAssetType} from './schemas'

type RentalAssetFormValues = {
    name: string
    rentalAssetTypeId: string | null
}

type RentalAssetFormModalProps = {
    opened: boolean
    rentalAssetTypes: RentalAssetType[]
    onClose: () => void
    onCreate: (input: CreateRentalAssetInput) => Promise<RentalAsset>
    onSaved: () => void
}

export function RentalAssetFormModal({
    opened,
    rentalAssetTypes,
    onClose,
    onCreate,
    onSaved,
}: RentalAssetFormModalProps) {
    const [isSubmitting, setIsSubmitting] = useState(false)

    const form = useForm<RentalAssetFormValues>({
        initialValues: {
            name: '',
            rentalAssetTypeId: null,
        },
        validate: {
            name: (value) => (value.trim() ? null : 'Asset name is required'),
            rentalAssetTypeId: (value) => (value ? null : 'Asset type is required'),
        },
    })

    useEffect(() => {
        if (!opened) {
            return
        }
        form.setValues({
            name: '',
            rentalAssetTypeId: rentalAssetTypes[0]?.id ?? null,
        })
        form.clearErrors()
        // eslint-disable-next-line react-hooks/exhaustive-deps -- reset modal fields when opened
    }, [opened, rentalAssetTypes])

    function handleClose() {
        form.reset()
        onClose()
    }

    async function handleSubmit(values: RentalAssetFormValues) {
        if (!values.rentalAssetTypeId) {
            return
        }

        setIsSubmitting(true)
        try {
            await onCreate({
                name: values.name.trim(),
                rentalAssetTypeId: values.rentalAssetTypeId,
            })
            onSaved()
            form.reset()
            onClose()
        } finally {
            setIsSubmitting(false)
        }
    }

    const typeOptions = rentalAssetTypes.map((type) => ({
        value: type.id,
        label: type.name,
    }))

    return (
        <Modal opened={opened} onClose={handleClose} title="Create rental asset" centered>
            <form onSubmit={form.onSubmit(handleSubmit)}>
                <Stack gap="md">
                    <TextInput label="Name" withAsterisk {...form.getInputProps('name')} />
                    <Select
                        label="Type"
                        withAsterisk
                        data={typeOptions}
                        {...form.getInputProps('rentalAssetTypeId')}
                    />

                    <Group justify="flex-end">
                        <Button type="button" variant="default" onClick={handleClose}>
                            Cancel
                        </Button>
                        <Button type="submit" loading={isSubmitting}>
                            Create asset
                        </Button>
                    </Group>
                </Stack>
            </form>
        </Modal>
    )
}
